import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, UserPlus, UserCheck, Activity, Bell, Search, 
  Shield, Trash2, UserX, MoreVertical, Eye, TrendingUp,
  Receipt, ShoppingBag, Clock, CheckCircle, XCircle, LogOut, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Use window.location.origin for API calls to work on any domain
const API = `${window.location.origin}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  
  // Notification testing state
  const [notificationTargets, setNotificationTargets] = useState([]);
  const [selectedTargetEmail, setSelectedTargetEmail] = useState('');
  const [selectedNotificationType, setSelectedNotificationType] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationLogs, setNotificationLogs] = useState([]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, targetsRes, logsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: 'include' }),
        fetch(`${API}/admin/users?search=${encodeURIComponent(search)}`, { credentials: 'include' }),
        fetch(`${API}/admin/notification-targets`, { credentials: 'include' }),
        fetch(`${API}/admin/notification-logs?limit=5`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users);
      }
      if (targetsRes.ok) {
        const data = await targetsRes.json();
        setNotificationTargets(data.targets);
      }
      if (logsRes.ok) {
        const data = await logsRes.json();
        setNotificationLogs(data.logs);
      }
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleSendNotification = async () => {
    if (!selectedTargetEmail || !selectedNotificationType) {
      toast.error('Please select a user and notification type');
      return;
    }

    // Check if user can receive notifications
    const targetUser = notificationTargets.find(t => t.email === selectedTargetEmail);
    if (targetUser && !targetUser.can_receive_notifications) {
      toast.error(`${targetUser.name || targetUser.email} has not enabled push notifications. They need to enable it in Settings first.`);
      return;
    }

    setSendingNotification(true);
    try {
      const response = await fetch(`${API}/admin/send-test-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_email: selectedTargetEmail,
          notification_type: selectedNotificationType
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || `✅ Notification sent to ${data.sent_count} device(s)`);
        // Refresh logs and targets
        const [logsRes, targetsRes] = await Promise.all([
          fetch(`${API}/admin/notification-logs?limit=5`, { credentials: 'include' }),
          fetch(`${API}/admin/notification-targets`, { credentials: 'include' })
        ]);
        if (logsRes.ok) {
          const logsData = await logsRes.json();
          setNotificationLogs(logsData.logs);
        }
        if (targetsRes.ok) {
          const targetsData = await targetsRes.json();
          setNotificationTargets(targetsData.targets);
        }
      } else {
        // Show detailed error
        if (data.error_detail) {
          toast.error(data.error_detail);
        } else {
          toast.error(data.message || 'Failed to send notification');
        }
      }
    } catch (error) {
      toast.error('Network error - failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/users?search=${encodeURIComponent(search)}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (user) => {
    setViewingUser(user);
    try {
      const res = await fetch(`${API}/admin/users/${user.user_id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setUserDetails(data);
      }
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleAction = async () => {
    if (!selectedUser || !actionType) return;

    try {
      let url, method;

      switch (actionType) {
        case 'delete':
          url = `${API}/admin/users/${selectedUser.user_id}`;
          method = 'DELETE';
          break;
        case 'suspend':
          url = `${API}/admin/users/${selectedUser.user_id}/deactivate`;
          method = 'PUT';
          break;
        case 'reactivate':
          url = `${API}/admin/users/${selectedUser.user_id}/reactivate`;
          method = 'PUT';
          break;
        case 'promote':
          url = `${API}/admin/users/${selectedUser.user_id}/role?role=admin`;
          method = 'PUT';
          break;
        case 'demote':
          url = `${API}/admin/users/${selectedUser.user_id}/role?role=user`;
          method = 'PUT';
          break;
        default:
          return;
      }

      const response = await fetch(url, { method, credentials: 'include' });

      if (response.ok) {
        toast.success(`User ${actionType}ed successfully`);
        fetchData();
      } else {
        throw new Error('Action failed');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSelectedUser(null);
      setActionType(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-[#0F1224] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#63E6E2]/30 border-t-[#63E6E2] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1224]" data-testid="admin-page">
      {/* Header */}
      <header className="px-4 pt-4 pb-2 safe-top border-b border-[#2D3354]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#63E6E2]/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#63E6E2]" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-[#6B7280] text-sm">Analytics & User Management</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444]"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Top Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#63E6E2]" />
              <span className="text-[#6B7280] text-xs uppercase tracking-wider">Total Users</span>
            </div>
            <p className="font-heading text-3xl font-bold text-white" data-testid="total-users">
              {stats?.total_users || 0}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-4 h-4 text-[#10B981]" />
              <span className="text-[#6B7280] text-xs uppercase tracking-wider">New Today</span>
            </div>
            <p className="font-heading text-3xl font-bold text-white" data-testid="new-signups-today">
              {stats?.new_signups_today || 0}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <UserCheck className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-[#6B7280] text-xs uppercase tracking-wider">Onboarded</span>
            </div>
            <p className="font-heading text-3xl font-bold text-white" data-testid="onboarded-users">
              {stats?.onboarded_users || 0}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-[#2E93fA]" />
              <span className="text-[#6B7280] text-xs uppercase tracking-wider">Active Today</span>
            </div>
            <p className="font-heading text-3xl font-bold text-white" data-testid="active-today">
              {stats?.active_users_today || 0}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-[#8B5CF6]" />
              <span className="text-[#6B7280] text-xs uppercase tracking-wider">Active 7 Days</span>
            </div>
            <p className="font-heading text-3xl font-bold text-white" data-testid="active-7-days">
              {stats?.active_users_7_days || 0}
            </p>
          </motion.div>
        </div>

        {/* Engagement Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
        >
          <h2 className="font-heading text-lg font-bold text-white mb-4">Engagement Metrics</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-[#252A4A] rounded-lg p-4 text-center">
              <ShoppingBag className="w-6 h-6 text-[#EF4444] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.total_spending_entries || 0}</p>
              <p className="text-[#6B7280] text-xs">Spending Entries</p>
            </div>
            <div className="bg-[#252A4A] rounded-lg p-4 text-center">
              <Receipt className="w-6 h-6 text-[#63E6E2] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.total_bills || 0}</p>
              <p className="text-[#6B7280] text-xs">Bills Created</p>
            </div>
            <div className="bg-[#252A4A] rounded-lg p-4 text-center">
              <Bell className="w-6 h-6 text-[#F59E0B] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.notification_opt_ins || 0}</p>
              <p className="text-[#6B7280] text-xs">Push Subscribers</p>
            </div>
            <div className="bg-[#252A4A] rounded-lg p-4 text-center">
              <UserCheck className="w-6 h-6 text-[#10B981] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">
                {stats?.total_users ? Math.round((stats.onboarded_users / stats.total_users) * 100) : 0}%
              </p>
              <p className="text-[#6B7280] text-xs">Onboarding Rate</p>
            </div>
          </div>
        </motion.div>

        {/* Notification Testing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.27 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#F59E0B]" />
              <h2 className="font-heading text-lg font-bold text-white">Notification Testing</h2>
            </div>
            <div className="text-[#6B7280] text-sm">
              {notificationTargets.filter(t => t.can_receive_notifications).length} users with push enabled
            </div>
          </div>
          
          <p className="text-[#6B7280] text-sm mb-4">
            Send real push notifications to any registered user. Users with ✅ have push enabled and can receive notifications.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {/* Target User Select */}
            <div>
              <label className="text-[#9CA3AF] text-xs uppercase tracking-wider block mb-2">Target User</label>
              <Select value={selectedTargetEmail} onValueChange={setSelectedTargetEmail}>
                <SelectTrigger className="bg-[#252A4A] border-[#2D3354] text-white h-12" data-testid="notification-target-select">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1B1F3A] border-[#2D3354] max-h-60">
                  {notificationTargets.length > 0 ? (
                    notificationTargets.map((target) => (
                      <SelectItem 
                        key={target.user_id} 
                        value={target.email}
                        className="text-white hover:bg-[#252A4A]"
                      >
                        <div className="flex items-center gap-2">
                          <span>{target.can_receive_notifications ? '✅' : '❌'}</span>
                          <span>{target.name || target.email}</span>
                          {target.can_receive_notifications && (
                            <span className="text-[#6B7280] text-xs">
                              ({target.subscription_count} device{target.subscription_count !== 1 ? 's' : ''})
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled className="text-[#6B7280]">
                      No users found
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {selectedTargetEmail && notificationTargets.find(t => t.email === selectedTargetEmail) && (
                <p className={`text-xs mt-1 ${
                  notificationTargets.find(t => t.email === selectedTargetEmail)?.can_receive_notifications
                    ? 'text-[#10B981]'
                    : 'text-[#EF4444]'
                }`}>
                  {notificationTargets.find(t => t.email === selectedTargetEmail)?.subscription_status_label}
                </p>
              )}
            </div>

            {/* Notification Type Select */}
            <div>
              <label className="text-[#9CA3AF] text-xs uppercase tracking-wider block mb-2">Notification Type</label>
              <Select value={selectedNotificationType} onValueChange={setSelectedNotificationType}>
                <SelectTrigger className="bg-[#252A4A] border-[#2D3354] text-white h-12" data-testid="notification-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1B1F3A] border-[#2D3354]">
                  <SelectItem value="daily_summary" className="text-white hover:bg-[#252A4A]">
                    ☀️ Morning Daily Summary (uses real data)
                  </SelectItem>
                  <SelectItem value="midday_reminder" className="text-white hover:bg-[#252A4A]">
                    🧾 Midday Spending Reminder (12 PM)
                  </SelectItem>
                  <SelectItem value="afternoon_reminder" className="text-white hover:bg-[#252A4A]">
                    🌆 Afternoon Check-in (6 PM)
                  </SelectItem>
                  <SelectItem value="evening_reminder" className="text-white hover:bg-[#252A4A]">
                    📝 Evening End of Day Reminder (10 PM)
                  </SelectItem>
                  <SelectItem value="on_track" className="text-white hover:bg-[#252A4A]">
                    🟢 On Track
                  </SelectItem>
                  <SelectItem value="tight" className="text-white hover:bg-[#252A4A]">
                    🟠 Tight
                  </SelectItem>
                  <SelectItem value="review_needed" className="text-white hover:bg-[#252A4A]">
                    🔴 Review Needed
                  </SelectItem>
                  <SelectItem value="bill_due" className="text-white hover:bg-[#252A4A]">
                    ⚡ Bill due tomorrow
                  </SelectItem>
                  <SelectItem value="payday_soon" className="text-white hover:bg-[#252A4A]">
                    💰 Payday Coming
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Send Button */}
            <div className="flex items-end">
              <Button
                onClick={handleSendNotification}
                disabled={sendingNotification || !selectedTargetEmail || !selectedNotificationType}
                className="w-full h-12 bg-[#F59E0B] text-white hover:bg-[#D97706] disabled:opacity-50"
                data-testid="send-notification-btn"
              >
                {sendingNotification ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Notification
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Recent Logs */}
          {notificationLogs.length > 0 && (
            <div className="border-t border-[#2D3354] pt-4 mt-4">
              <h3 className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-3">Recent Test Logs</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {notificationLogs.map((log, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                      log.sent_count > 0 ? 'bg-[#10B981]/10' : 'bg-[#EF4444]/10'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {log.sent_count > 0 ? (
                        <CheckCircle className="w-4 h-4 text-[#10B981]" />
                      ) : (
                        <XCircle className="w-4 h-4 text-[#EF4444]" />
                      )}
                      <span className="text-white">{log.notification_type}</span>
                      <span className="text-[#6B7280]">→</span>
                      <span className="text-[#9CA3AF]">{log.target_email}</span>
                    </div>
                    <span className="text-[#6B7280] text-xs">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex gap-2"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 h-12 bg-[#1B1F3A] border-[#2D3354] text-white placeholder:text-[#6B7280] focus:border-[#63E6E2]"
              data-testid="user-search-input"
            />
          </div>
          <Button
            onClick={handleSearch}
            className="h-12 px-6 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF]"
            data-testid="search-btn"
          >
            Search
          </Button>
        </motion.div>

        {/* Users Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] overflow-hidden"
        >
          <div className="p-4 border-b border-[#2D3354]">
            <h2 className="font-heading text-lg font-bold text-white">Users ({users.length})</h2>
          </div>
          
          {/* Table Header */}
          <div className="hidden lg:grid grid-cols-9 gap-4 px-4 py-3 bg-[#252A4A] text-[#6B7280] text-xs uppercase tracking-wider font-medium">
            <div className="col-span-2">User</div>
            <div>Signup Date</div>
            <div>Onboarded</div>
            <div>Push</div>
            <div>Last Login</div>
            <div>Spending</div>
            <div>Bills</div>
            <div>Actions</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-[#2D3354]">
            {users.length > 0 ? (
              users.map((user) => (
                <div
                  key={user.user_id}
                  className="p-4 hover:bg-[#252A4A]/50 transition-colors"
                  data-testid={`user-row-${user.user_id}`}
                >
                  {/* Mobile View */}
                  <div className="lg:hidden space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#252A4A] border border-[#2D3354] flex items-center justify-center">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <span className="text-white font-medium">{user.name?.[0]?.toUpperCase() || 'U'}</span>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{user.name}</p>
                            {user.role === 'admin' && <Shield className="w-4 h-4 text-[#63E6E2]" />}
                            {user.suspended && <XCircle className="w-4 h-4 text-[#EF4444]" />}
                            {user.push_enabled && <Bell className="w-4 h-4 text-[#F59E0B]" title="Push enabled" />}
                          </div>
                          <p className="text-[#6B7280] text-sm">{user.email}</p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 text-[#6B7280] hover:text-white hover:bg-white/5 rounded-lg">
                            <MoreVertical className="w-5 h-5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1B1F3A] border-[#2D3354]">
                          <DropdownMenuItem onClick={() => handleViewUser(user)} className="text-white hover:bg-white/10">
                            <Eye className="w-4 h-4 mr-2" /> View
                          </DropdownMenuItem>
                          {user.suspended ? (
                            <DropdownMenuItem onClick={() => { setSelectedUser(user); setActionType('reactivate'); }} className="text-[#10B981] hover:bg-[#10B981]/10">
                              <CheckCircle className="w-4 h-4 mr-2" /> Reactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => { setSelectedUser(user); setActionType('suspend'); }} className="text-[#F59E0B] hover:bg-[#F59E0B]/10">
                              <UserX className="w-4 h-4 mr-2" /> Suspend
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => { setSelectedUser(user); setActionType('delete'); }} className="text-[#EF4444] hover:bg-[#EF4444]/10">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      <div>
                        <p className="text-[#6B7280] text-xs">Signup</p>
                        <p className="text-white text-sm">{formatShortDate(user.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-[#6B7280] text-xs">Onboarded</p>
                        <p className={`text-sm ${user.onboarded ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                          {user.onboarded ? 'Yes' : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#6B7280] text-xs">Push</p>
                        <p className={`text-sm ${user.push_enabled ? 'text-[#F59E0B]' : 'text-[#6B7280]'}`}>
                          {user.push_enabled ? '✅' : '❌'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#6B7280] text-xs">Spending</p>
                        <p className="text-white text-sm">{user.spending_entries || 0}</p>
                      </div>
                      <div>
                        <p className="text-[#6B7280] text-xs">Bills</p>
                        <p className="text-white text-sm">{user.bills_count || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* Desktop View */}
                  <div className="hidden lg:grid grid-cols-9 gap-4 items-center">
                    <div className="col-span-2 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#252A4A] border border-[#2D3354] flex items-center justify-center flex-shrink-0">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="text-white font-medium">{user.name?.[0]?.toUpperCase() || 'U'}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium truncate">{user.name}</p>
                          {user.role === 'admin' && <Shield className="w-4 h-4 text-[#63E6E2] flex-shrink-0" />}
                          {user.suspended && <XCircle className="w-4 h-4 text-[#EF4444] flex-shrink-0" />}
                        </div>
                        <p className="text-[#6B7280] text-sm truncate">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-[#9CA3AF] text-sm">{formatShortDate(user.created_at)}</div>
                    <div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.onboarded 
                          ? 'bg-[#10B981]/10 text-[#10B981]' 
                          : 'bg-[#EF4444]/10 text-[#EF4444]'
                      }`}>
                        {user.onboarded ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.push_enabled 
                          ? 'bg-[#F59E0B]/10 text-[#F59E0B]' 
                          : 'bg-[#6B7280]/10 text-[#6B7280]'
                      }`} title={user.push_enabled ? `${user.push_subscription_count} device(s)` : 'Not enabled'}>
                        {user.push_enabled ? `✅ ${user.push_subscription_count}` : '❌'}
                      </span>
                    </div>
                    <div className="text-[#9CA3AF] text-sm">{formatShortDate(user.last_login)}</div>
                    <div className="text-white font-medium">{user.spending_entries || 0}</div>
                    <div className="text-white font-medium">{user.bills_count || 0}</div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleViewUser(user)}
                        className="p-2 text-[#6B7280] hover:text-white hover:bg-white/5 rounded-lg"
                        title="View User"
                        data-testid={`view-user-${user.user_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {user.suspended ? (
                        <button
                          onClick={() => { setSelectedUser(user); setActionType('reactivate'); }}
                          className="p-2 text-[#10B981] hover:bg-[#10B981]/10 rounded-lg"
                          title="Reactivate User"
                          data-testid={`reactivate-user-${user.user_id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => { setSelectedUser(user); setActionType('suspend'); }}
                          className="p-2 text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded-lg"
                          title="Suspend User"
                          data-testid={`suspend-user-${user.user_id}`}
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => { setSelectedUser(user); setActionType('delete'); }}
                        className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg"
                        title="Delete User"
                        data-testid={`delete-user-${user.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto mb-3 text-[#6B7280]" />
                <p className="text-white font-medium">No users found</p>
                <p className="text-[#6B7280] text-sm">Try a different search term</p>
              </div>
            )}
          </div>
        </motion.div>
      </main>

      {/* User Details Modal */}
      <Dialog open={!!viewingUser} onOpenChange={() => { setViewingUser(null); setUserDetails(null); }}>
        <DialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-lg mx-auto max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#63E6E2]" />
              User Details
            </DialogTitle>
          </DialogHeader>
          
          {userDetails ? (
            <div className="space-y-4 mt-4">
              {/* User Info */}
              <div className="flex items-center gap-4 p-4 bg-[#252A4A] rounded-lg">
                <div className="w-14 h-14 rounded-full bg-[#2D3354] flex items-center justify-center">
                  {userDetails.user?.picture ? (
                    <img src={userDetails.user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-white text-xl font-bold">{userDetails.user?.name?.[0]?.toUpperCase() || 'U'}</span>
                  )}
                </div>
                <div>
                  <p className="text-white font-semibold text-lg">{userDetails.user?.name}</p>
                  <p className="text-[#6B7280]">{userDetails.user?.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {userDetails.user?.role === 'admin' && (
                      <span className="px-2 py-0.5 bg-[#63E6E2]/10 text-[#63E6E2] text-xs rounded-full">Admin</span>
                    )}
                    {userDetails.user?.onboarded && (
                      <span className="px-2 py-0.5 bg-[#10B981]/10 text-[#10B981] text-xs rounded-full">Onboarded</span>
                    )}
                    {userDetails.user?.suspended && (
                      <span className="px-2 py-0.5 bg-[#EF4444]/10 text-[#EF4444] text-xs rounded-full">Suspended</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Account Stats - Operational metrics only, NO financial data */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#252A4A] rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{userDetails.engagement_counts?.spending_entries || 0}</p>
                  <p className="text-[#6B7280] text-xs">Entries</p>
                </div>
                <div className="bg-[#252A4A] rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{userDetails.engagement_counts?.bills || 0}</p>
                  <p className="text-[#6B7280] text-xs">Bills</p>
                </div>
                <div className="bg-[#252A4A] rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{userDetails.push_status?.device_count || 0}</p>
                  <p className="text-[#6B7280] text-xs">Devices</p>
                </div>
              </div>

              {/* Account Information */}
              <div className="space-y-3">
                <h3 className="text-[#6B7280] text-xs uppercase tracking-wider font-medium">Account Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-[#252A4A] rounded-lg p-3">
                    <p className="text-[#6B7280] text-xs mb-1">Timezone</p>
                    <p className="text-white">{userDetails.user?.timezone || 'Not set'}</p>
                  </div>
                  <div className="bg-[#252A4A] rounded-lg p-3">
                    <p className="text-[#6B7280] text-xs mb-1">Push Status</p>
                    <p className={userDetails.push_status?.enabled ? 'text-[#10B981]' : 'text-[#6B7280]'}>
                      {userDetails.push_status?.enabled ? `✅ Enabled` : '❌ Disabled'}
                    </p>
                  </div>
                  <div className="bg-[#252A4A] rounded-lg p-3">
                    <p className="text-[#6B7280] text-xs mb-1">Signup Date</p>
                    <p className="text-white">{formatDate(userDetails.user?.created_at)}</p>
                  </div>
                  <div className="bg-[#252A4A] rounded-lg p-3">
                    <p className="text-[#6B7280] text-xs mb-1">Last Activity</p>
                    <p className="text-white">{formatDate(userDetails.activity?.last_spending_date) || 'No activity'}</p>
                  </div>
                </div>
              </div>

              {/* Recent Sessions */}
              {userDetails.activity?.recent_sessions?.length > 0 && (
                <div>
                  <h3 className="text-[#6B7280] text-xs uppercase tracking-wider font-medium mb-2">Recent Logins</h3>
                  <div className="space-y-1">
                    {userDetails.activity.recent_sessions.slice(0, 3).map((session, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-[#6B7280]" />
                        <span className="text-[#9CA3AF]">{formatDate(session.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Privacy Notice */}
              <div className="bg-[#252A4A] rounded-lg p-3 border border-[#2D3354]">
                <p className="text-[#6B7280] text-xs flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Financial data (balance, amounts, transactions) is private to this user
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-[#63E6E2]/30 border-t-[#63E6E2] rounded-full animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <AlertDialog open={!!selectedUser && !!actionType} onOpenChange={() => {
        setSelectedUser(null);
        setActionType(null);
      }}>
        <AlertDialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              {actionType === 'delete' && 'Delete User?'}
              {actionType === 'suspend' && 'Suspend User?'}
              {actionType === 'reactivate' && 'Reactivate User?'}
              {actionType === 'promote' && 'Promote to Admin?'}
              {actionType === 'demote' && 'Remove Admin Role?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              {actionType === 'delete' && `This will permanently delete ${selectedUser?.name}'s account and all their data. This cannot be undone.`}
              {actionType === 'suspend' && `${selectedUser?.name} will be suspended and unable to log in.`}
              {actionType === 'reactivate' && `${selectedUser?.name} will be able to log in again.`}
              {actionType === 'promote' && `${selectedUser?.name} will have full admin access.`}
              {actionType === 'demote' && `${selectedUser?.name} will lose admin privileges.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#2D3354] text-white hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={
                actionType === 'delete' 
                  ? 'bg-[#EF4444] text-white hover:bg-[#DC2626]'
                  : actionType === 'suspend'
                  ? 'bg-[#F59E0B] text-white hover:bg-[#D97706]'
                  : 'bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF]'
              }
              data-testid="confirm-action-btn"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
